import { ActionItem } from '../../../types/button/ActionButton';
import { SalePermissions } from './salePermissions';

export const getSaleTableActions = ({
  row,
  permissions,
  handlers,
}: {
  row: any;
  permissions: SalePermissions;
  handlers: {
    onView: (id: number, isCancelled?: boolean) => void;
    onCancel: (id: number) => void;
    onContinue: (id: number) => void;
    onEdit: (id: number) => void;
    onDownloadPdf: (params: { id: number; branchId: number }) => void;
    onConsultDTE: (id: number) => void;
    onExportJson: (sale: any) => void;
    onViewInvalidDte: (id: number) => void;
    onReprintTicket: (params: { type: string; id: number }) => void;
    onForceAnnul: (id: number) => void;
    onDeleteRejected: (id: number) => void;
    onResendDTE: (id: number) => void;
    onSendEmail: (id: number) => void;
    onViewDteError: (id: number) => void;
    onSupportEdit: (id: number) => void;
    onCreateReturn: (id: number) => void;
    onCreateSpecialOperation: (id: number) => void;
  };
}): ActionItem[] => {
  const actions: ActionItem[] = [];

  // Helpers reutilizables (mismo permiso en cualquier estado de la venta).
  const pushView = (label: string) =>
    actions.push({ key: 'view', label, onClick: () => handlers.onView(row.id) });

  const pushPdf = () => {
    if (!permissions.canViewPdf) return;
    actions.push({
      key: 'pdf',
      label: 'PDF',
      onClick: () => handlers.onDownloadPdf({ id: row.id, branchId: row.branchId }),
    });
  };

  const pushConsultDte = () => {
    if (!permissions.canConsultDte) return;
    actions.push({
      key: 'dte',
      label: 'Consultar DTE',
      onClick: () => handlers.onConsultDTE(row.id),
    });
  };

  if (
    permissions.canViewHaciendaResponse &&
    (row.hasHaciendaResponse || row.hasDteError)
  ) {
    const rejected = row.hasDteError === true;

    actions.push({
      key: rejected ? 'dte-error' : 'dte-response',
      label: rejected
        ? 'Ver rechazo de Hacienda'
        : 'Ver respuesta de Hacienda',
      onClick: () => handlers.onViewDteError(row.id),
    });
  }

  if (row.status === 'DRAFT_CANCELLED') {
    if (permissions.canSupportView) {
      pushView('Ver borrador descartado');
      if (permissions.canSupportEdit) actions.push({ key: 'support', label: 'Corrección de soporte', onClick: () => handlers.onSupportEdit(row.id) });
    }
    return actions;
  }

  if (permissions.canSupportEdit) {
    actions.push({ key: 'support', label: 'Corrección de soporte', onClick: () => handlers.onSupportEdit(row.id) });
  }

  // =====================================================
  // PENDIENTE
  // =====================================================

  if (row.status === 'PENDING') {
    actions.push({
      key: 'continue',
      label: 'Continuar',
      onClick: () => handlers.onContinue(row.id),
    });

    return actions;
  }

  // =====================================================
  // FINALIZADA / DTE PROCESADO
  // =====================================================

  if (row.status === 'FINALIZED' || row.status === 'DTE_PROCESSED') {
    pushView('Ver Venta');
    pushPdf();

    if (permissions.canSendEmail) {
      actions.push({
        key: 'email',
        label: 'Enviar por Correo',
        onClick: () => handlers.onSendEmail(row.id),
      });
    }

    pushConsultDte();

    if (permissions.canViewJson) {
      actions.push({
        key: 'json',
        label: 'Descargar JSON',
        onClick: () => handlers.onExportJson(row.id),
      });
    }

    if (permissions.canReprint) {
      actions.push({
        key: 'reprint',
        label: 'Imprimir ahora / Reintentar',
        onClick: () => handlers.onReprintTicket({ type: 'SALE', id: row.id }),
      });
    }

    if (permissions.canCancel) {
      actions.push({
        key: 'cancel',
        label: 'Anular Venta',
        onClick: () => handlers.onCancel(row.id),
      });
    }
    if (row.returnEligible === true && permissions.canCreateReturn) {
      actions.push({ key: 'return-event', label: 'Crear evento de retorno', onClick: () => handlers.onCreateReturn(row.id) });
    }
    // Operaciones Especiales: módulo no activo todavía, entrada deshabilitada a propósito.
    // if (row.specialOperationEligible === true && permissions.canCreateReturn) {
    //   actions.push({ key: 'special-operation', label: 'Generar operación especial', onClick: () => handlers.onCreateSpecialOperation(row.id) });
    // }
  }

  // =====================================================
  // ANULADA
  // =====================================================

  if (row.status === 'CANCELED') {
    pushView('Ver Venta Anulada');
    actions.push({
      key: 'cancel',
      label: 'Ver DTE Invalidado',
      onClick: () => handlers.onViewInvalidDte(row.id),
    });
    pushConsultDte();
  }

  // =====================================================
  // DTE PENDIENTE
  // =====================================================

  if (row.status === 'DTE_PENDING') {
    pushView('Ver Venta');
    pushPdf();
    pushConsultDte();
  }

  // =====================================================
  // PENDIENTE DE CAJA
  // =====================================================

  if (row.status === 'PENDING_CASHIER') {
    pushView('Ver Venta');

    if (permissions.canEditPendingCashier) {
      actions.push({
        key: 'edit',
        label: 'Editar Venta',
        onClick: () => handlers.onEdit(row.id),
      });
    }

    return actions;
  }

  // =====================================================
  // DTE ENVIADO
  // =====================================================

  if (row.status === 'DTE_SENT') {
    pushView('Ver Venta');
    pushPdf();
    pushConsultDte();
  }

  // =====================================================
  // DTE RECHAZADO
  // =====================================================

  if (row.status === 'DTE_REJECTED') {
    pushView('Ver Venta (Rechazada)');
    pushConsultDte();
    pushPdf();

    if (permissions.canResendDte) {
      actions.push({
        key: 'resend',
        label: 'Reenviar DTE',
        onClick: () => handlers.onResendDTE(row.id),
      });
    }

    if (permissions.canForceAnnul) {
      actions.push({
        key: 'cancel',
        label: 'Anulación Técnica',
        onClick: () => handlers.onForceAnnul(row.id),
      });
    }

    if (permissions.canDeleteRejected) {
      actions.push({
        key: 'trash',
        label: 'Eliminar Venta',
        onClick: () => handlers.onDeleteRejected(row.id),
      });
    }

    return actions;
  }

  // =====================================================
  // RECHAZADA
  // =====================================================

  if (row.status === 'REJECTED') {
    pushView('Ver Venta (Rechazada)');

    if (permissions.canForceAnnul) {
      actions.push({
        key: 'cancel',
        label: 'Liberar Correlativo',
        onClick: () => handlers.onForceAnnul(row.id),
      });
    }

    return actions;
  }

  // =====================================================
  // ACCIONES GLOBALES
  // =====================================================

  if (permissions.canResendDte) {
    actions.push({
      key: 'resend',
      label: 'Reenviar DTE',
      onClick: () => handlers.onResendDTE(row.id),
    });
  }

  // "Editar Venta" global: aplica a ventas ya consolidadas (FINALIZED /
  // DTE_PROCESSED / DTE_SENT / DTE_PENDING / CANCELED). El Vendedor NO puede
  // editar estas (solo PENDING / PENDING_CASHIER, más arriba con canEdit).
  if (permissions.canEditFinalized) {
    actions.push({
      key: 'edit',
      label: 'Editar Venta',
      onClick: () => handlers.onEdit(row.id),
    });
  }

  return actions;
};
